<script setup>
import DropdownComponent from '@/components/DropdownComponent.vue';
import { useClickAway } from '@/utils';
import { useSlots, ref, computed, watch } from 'vue';

const props = defineProps({
  wrapperClass: {
    type: String,
    required: false,
    default: '',
  },
  inputType: {
    type: String,
    required: false,
    default: 'text',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  isDisabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideInput: {
    type: Boolean,
    required: false,
    default: false,
  },
  showClearButton: {
    type: Boolean,
    required: false,
    default: true,
  },
  dropdownList: {
    type: Array,
    required: false,
    default: () => [],
  },
  canShowDropdown: {
    type: Boolean,
    required: false,
    default: true,
  },
  inputLabel: {
    type: String,
    required: false,
    default: '',
  },
  hasBadge: {
    type: Boolean,
    required: false,
    default: false,
  },
  badgeText: {
    type: String,
    required: false,
    default: '',
  },
  hasCaretDown: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasAutoComplete: {
    type: Boolean,
    required: false,
    default: false,
  },
  hasError: {
    type: Boolean,
    required: false,
    default: false,
  },
  defaultInputValue: {
    type: [String, Number],
    required: false,
    default: '',
  },
  value: {
    type: [String, Number],
    required: false,
    default: '',
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
  isCustomTextSize: {
    type: Boolean,
    required: false,
    default: false,
  },
  pattern: {
    type: String,
    required: false,
    default: '',
  },
  maxLength: {
    type: String,
    required: false,
    default: '',
  },
  minLength: {
    type: String,
    required: false,
    default: '',
  },
  title: {
    type: String,
    required: false,
    default: '',
  },
  onlyUseWrapperClass: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showDropdown = ref(false);
const inputValue = ref(props.defaultInputValue);
// canShowDropdown is a prop that determines if the dropdown should be shown based on additional conditions
const isDropdownVisible = computed(
  () => showDropdown.value && props.canShowDropdown
);

const inputWrapperRef = ref(null);
useClickAway(inputWrapperRef, () => {
  showDropdown.value = false;
});

watch(
  () => props.value,
  (newValue) => {
    inputValue.value = newValue;
  }
);

const emit = defineEmits([
  'wasClicked',
  'isDirty',
  'enterPress',
  'hoverOn',
  'hoverOff',
  'inputChange',
  'inputClick',
  'onBlur',
  'onFocus',
  'dropdownClick',
  'badgeClick',
]);

const handleInputChange = (event) => {
  inputValue.value = event.target.value;
  if (!inputValue.value.length) {
    showDropdown.value = false;
  } else {
    showDropdown.value = true;
  }
  emit('inputChange', event.target.value, props.inputLabel);
};

const handleInputClick = (event) => {
  isFocused.value = true;
  if (!inputValue.value.length) {
    showDropdown.value = false;
  } else {
    showDropdown.value = true;
  }

  emit('inputClick', event.target.value, props.inputLabel);
};

const handleClearButtonClick = () => {
  inputValue.value = '';
  emit('inputChange', inputValue.value, props.inputLabel);
};

const isFocused = ref(false);
const handleFocus = (event) => {
  isFocused.value = true;

  emit('onFocus', event.target.value, props.inputLabel);
};

const handleBlur = (event) => {
  isFocused.value = false;
  emit('onBlur', event.target.value, props.inputLabel);
};

const handleBadgeClick = () => {
  emit('badgeClick', props.inputLabel);
};
const slots = useSlots();

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
};

const handleDropdownClick = (item) => {
  showDropdown.value = false;
  emit('dropdownClick', props.inputLabel, item);
};
</script>

<template>
  <div
    ref="inputWrapperRef"
    :class="
      props.onlyUseWrapperClass
        ? props.wrapperClass
        : hasError && !isFocused
          ? props.wrapperClass +
            ' ' +
            'default-input-wrapper relative rounded-lg bg-steel-100 outline outline-1 outline-offset-[-1px] outline-rose-500'
          : isFocused
            ? props.wrapperClass +
              ' ' +
              'default-input-wrapper outline-neutral-ring relative rounded-lg bg-white outline outline-1 outline-offset-[-1px]'
            : props.wrapperClass +
              ' ' +
              'default-input-wrapper default-input-wrapper-border hover:bg-info-subtle-bg relative rounded-lg bg-steel-100'
    "
  >
    <div
      v-if="slots.leftIcon"
      class="h-6 w-6"
    >
      <slot name="leftIcon"></slot>
    </div>

    <div
      v-if="hideInput"
      id="origin-badge"
      class="flex items-center justify-center gap-2 rounded-full bg-dark-gray px-2.5 py-0.5 text-base text-white"
    >
      <div class="whitespace-nowrap text-sm">{{ badgeText }}</div>
      <div
        class="h-4 w-4 cursor-pointer hover:opacity-80"
        @click.stop="handleBadgeClick"
      >
        <svg
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </div>
    </div>

    <input
      v-else
      :id="inputLabel"
      :name="inputLabel"
      :title="title"
      class="custom-input"
      :class="{ 'text-[inherit]': isCustomTextSize }"
      :pattern="pattern || undefined"
      :maxlength="maxLength || undefined"
      :minlength="minLength || undefined"
      :type="inputType"
      :placeholder="placeholder"
      :value="inputValue"
      :disabled="isDisabled"
      :readonly="readonly"
      :autocomplete="hasAutoComplete ? 'on' : 'off'"
      :aria-label="inputLabel"
      :aria-labelledby="inputLabel"
      :aria-describedby="inputLabel"
      @input="handleInputChange"
      @click="handleInputClick"
      @mouseover="emit('hoverOn')"
      @mouseleave="emit('hoverOff')"
      @keydown="emit('isDirty')"
      @keyup.enter="emit('enterPress')"
      @blur="handleBlur"
      @focus="handleFocus"
    />

    <div
      v-if="!hideInput && showClearButton && inputValue?.length > 0"
      class="active:text-selected-tab-text absolute right-3 hover:cursor-pointer hover:text-neutral-blue"
      :class="{
        'right-6': slots.rightIcon,
      }"
      @click="handleClearButtonClick"
    >
      <img
        class="h-2.5 w-2.5 opacity-90"
        src="@/assets/close-icon.svg"
        alt="close"
      />
    </div>

    <div
      v-if="slots.rightIcon"
      class="absolute right-0 h-6 w-6"
    >
      <slot name="rightIcon"></slot>
    </div>

    <button
      v-if="hasCaretDown"
      type="button"
      @click.stop="toggleDropdown"
    >
      <img
        class="opacity-50"
        src="@/assets/arrow-down-black.svg"
        alt="carrot"
      />
    </button>

    <DropdownComponent
      :dropdown-list="dropdownList"
      :is-dropdown-visible="isDropdownVisible && dropdownList.length > 0"
      :source="inputLabel"
      @dropdown-click="handleDropdownClick"
    />
  </div>
</template>

<style scoped>
.custom-input {
  @apply flex-grow rounded-none border-none bg-transparent p-0 font-normal leading-tight tracking-tight text-inherit text-steel-900 shadow-none outline-none;
}

.custom-input::placeholder {
  color: rgba(92, 107, 138, 1);
  line-height: 1.25;
  letter-spacing: -0.025em;
  font-weight: 400;
}

.default-input-wrapper-border {
  outline: 1px solid rgba(31, 36, 46, 0.14);
}
</style>
